import type { RegionId, Substance } from '../pharmacology/types';
import { RECEPTORS, RECEPTOR_TO_REGIONS, type ReceptorId } from '../pharmacology/receptors';
import { REGIONS } from '../pharmacology/regions';
import { citationsForSubstance } from '../pharmacology/citations';
import { requireElement } from './domHelpers';

const DEFAULT_MECHANISM = 'Default mode network active — no substance selected.';

export class InfoPanel {
  private readonly emojiEl: HTMLElement;
  private readonly nameEl: HTMLElement;
  private readonly mechanismEl: HTMLElement;
  private readonly receptorsEl: HTMLElement;
  private readonly regionsEl: HTMLElement;
  private readonly citationsEl: HTMLElement;

  constructor() {
    this.emojiEl = requireElement('panel-emoji');
    this.nameEl = requireElement('panel-name');
    this.mechanismEl = requireElement('panel-mechanism');
    this.receptorsEl = requireElement('panel-receptors');
    this.regionsEl = requireElement('panel-regions');
    this.citationsEl = requireElement('panel-citations');
  }

  show(substance: Substance | null): void {
    if (!substance) {
      this.emojiEl.textContent = '🧠';
      this.nameEl.textContent = 'Resting State';
      this.mechanismEl.textContent = DEFAULT_MECHANISM;
      this.receptorsEl.textContent = '';
      this.regionsEl.innerHTML = '';
      this.citationsEl.innerHTML = '';
      return;
    }

    this.emojiEl.textContent = substance.emoji;
    this.nameEl.textContent = substance.displayName;
    this.mechanismEl.textContent = substance.mechanismSummary;

    const receptorNames = substance.bindings
      .map((b) => RECEPTORS[b.receptor as ReceptorId]?.displayName ?? b.receptor)
      .join(', ');
    this.receptorsEl.textContent = `Receptors: ${receptorNames}`;

    const regionIds = new Set<RegionId>();
    for (const binding of substance.bindings) {
      const regions = RECEPTOR_TO_REGIONS[binding.receptor as ReceptorId] ?? [];
      for (const regionId of regions) regionIds.add(regionId);
    }
    this.regionsEl.innerHTML = Array.from(regionIds)
      .map((id) => `<span class="region-tag">${REGIONS[id].displayName}</span>`)
      .join('');

    this.citationsEl.innerHTML = citationsForSubstance(substance)
      .map((citation) => `<p>${escapeHtml(citation)}</p>`)
      .join('');
  }
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
